import { formatFeeAmount, formatPrice } from 'V2/Utilities/Formatters';
import { Fee } from 'V2/Types/FeeStrategy';
import { PaymentPlanType, PaymentScheduleType } from 'V2/Types/Payment';
import {
  getPaymentScheduleItemsByType,
  getPaymentScheduleEntry,
  toTitleCase,
  percentStr,
  isInstallmentFee,
} from 'V2/Utilities/FeeStrategy';

type FeeItem = PaymentScheduleType['items'][number];
type FeeLine = { id?: string; label: string; amount: string };

// Build a normalized list of fee lines by doing searching
// the PaymentSchedule items in the FeeStrategy fees
// - If a FeeItem.name matches a Fee.name, use the Fee.displayName as label
// - Otherwise, Title-Case the FeeItem.name as label
// - amount is the formatted amount via formatPrice
// Ordering: non-found items first (Title-Cased), then found items (using displayName)
export const buildFeeLines = (feeItems: FeeItem[], fees: Fee[]): FeeLine[] => {
  const feeByName = new Map<string, Fee>(
    fees.map((f) => [f.name.toLowerCase(), f])
  );

  const notFound: FeeLine[] = [];
  const found: FeeLine[] = [];

  for (const item of feeItems) {
    const match = feeByName.get(item.name.toLowerCase());
    const amount = formatPrice(item.amount);

    if (!match) {
      notFound.push({ id: item.name, label: toTitleCase(item.name), amount });
    } else {
      found.push({ id: item.name, label: match.displayName, amount });
    }
  }

  return [...notFound, ...found];
};

export const buildDownPaymentFeeLines = (paymentPlan: PaymentPlanType) => {
  const downPayment = getPaymentScheduleEntry(
    paymentPlan.paymentSchedule,
    'DOWNPAYMENT'
  );

  const feeItems = getPaymentScheduleItemsByType(
    downPayment?.items || [],
    'FEE'
  );

  return buildFeeLines(feeItems, paymentPlan.feeStrategy.fees);
};

export const buildInstallmentFeeLines = (paymentPlan: PaymentPlanType) => {
  const installment = getPaymentScheduleEntry(
    paymentPlan.paymentSchedule,
    'INSTALLMENT'
  );

  const feeItems = getPaymentScheduleItemsByType(
    installment?.items || [],
    'FEE'
  );

  // Exclude the installment fee from the general lines to avoid duplication
  const generalItems = feeItems.filter(
    (feeItem) => !isInstallmentFee(feeItem.name)
  );

  const installmentChargeLines = buildInstallmentChargeLines(
    feeItems,
    paymentPlan.feeStrategy.fees
  );

  return [
    ...buildFeeLines(generalItems, paymentPlan.feeStrategy.fees),
    ...installmentChargeLines,
  ];
};

const buildInstallmentChargeLines = (
  feeItems: FeeItem[],
  fees: Fee[]
): FeeLine[] => {
  const hasInstallmentFeeItem = feeItems.some((i) => isInstallmentFee(i.name));

  if (!hasInstallmentFeeItem) return [];

  const fee = fees.find((f) => isInstallmentFee(f.name));

  if (!fee) return [];

  const charge = fee.charge;

  if (charge.type === 'PERCENT') {
    const percentAmount = Number.isFinite(charge.percent) ? charge.percent : 0;
    return [
      {
        id: fee.name,
        label: 'Installment Fee',
        amount: `${percentStr(percentAmount)} simple interest`,
      },
    ];
  }

  if (charge.type === 'FLAT') {
    const amt = Number.isFinite(charge.amount) ? charge.amount : 0;
    return [
      {
        id: fee.name,
        label: 'Installment Fee',
        amount: formatFeeAmount(amt),
      },
    ];
  }

  return [];
};
